import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Chip,
  Stack,
  Paper,
  alpha,
  useTheme
} from '@mui/material'
import { format } from 'date-fns'
import FileStatusWorkflow from '../FileStatusWorkflow'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import PersonIcon from '@mui/icons-material/Person'
import EventNoteIcon from '@mui/icons-material/EventNote'
import InfoIcon from '@mui/icons-material/Info'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'

const MRDFileDetail = ({ file, onStatusChange }) => {
  const theme = useTheme()

  if (!file) {
    return (
      <Card sx={{ 
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CardContent sx={{ textAlign: 'center', py: 8 }}>
          <MedicalServicesIcon 
            sx={{ 
              fontSize: 64, 
              color: 'text.secondary',
              mb: 2,
              opacity: 0.5
            }} 
          />
          <Typography 
            variant="h6" 
            color="text.secondary"
            fontWeight={500}
          >
            Select a file to view details
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ 
      mb: 3, 
      p: 2,
      borderRadius: 2,
      bgcolor: alpha(theme.palette.primary.light, 0.05),
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        {icon}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          fontWeight={600}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography 
        variant="h6" 
        fontWeight={500}
        sx={{ 
          color: value === '-' ? 'text.disabled' : 'text.primary',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  )

  return (
    <Card sx={{ 
      borderRadius: 3,
      height: '100%',
      overflow: 'auto'
    }}>
      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          <MedicalServicesIcon 
            sx={{ 
              fontSize: 32, 
              color: 'primary.main',
              mr: 2
            }} 
          />
          <Typography 
            variant="h4" 
            fontWeight={700}
            color="primary"
          >
            MRD File Details
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Patient Information Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.08)
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <PersonIcon color="primary" />
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  color="primary"
                >
                  Patient Information
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="IPD Number" 
                    value={file.ipdNumber}
                    icon={<LocalHospitalIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="UHID" 
                    value={file.uhid}
                    icon={<LocalHospitalIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="Patient Name" 
                    value={file.patientName}
                    icon={<PersonIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <InfoRow 
                    label="Age" 
                    value={file.age}
                    icon={<InfoIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <InfoRow 
                    label="Gender" 
                    value={file.gender}
                    icon={<InfoIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Admission Details Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.info.main, 0.08)
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <EventNoteIcon color="info" />
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  color="info.main"
                >
                  Admission Details
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="Admission Date" 
                    value={file.admissionDate ? format(new Date(file.admissionDate), 'dd MMM yyyy') : '-'}
                    icon={<EventNoteIcon sx={{ fontSize: 20, color: 'info.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="Discharge Date" 
                    value={file.dischargeDate ? format(new Date(file.dischargeDate), 'dd MMM yyyy') : '-'}
                    icon={<EventNoteIcon sx={{ fontSize: 20, color: 'info.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="Ward" 
                    value={file.ward?.name || '-'}
                    icon={<LocalHospitalIcon sx={{ fontSize: 20, color: 'info.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="Doctor" 
                    value={file.doctor?.name ? `Dr. ${file.doctor.name}` : '-'}
                    icon={<PersonIcon sx={{ fontSize: 20, color: 'info.main' }} />}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* File Status Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.warning.main, 0.08)
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <InfoIcon color="warning" />
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  color="warning.main"
                >
                  File Status
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      fontWeight={600}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}
                    >
                      Status
                    </Typography>
                    <Chip 
                      label={file.fileStatus} 
                      color={
                        file.fileStatus === 'COMPLETED' ? 'success' :
                        file.fileStatus === 'IN_PROGRESS' ? 'warning' :
                        'default'
                      }
                      sx={{ 
                        height: 36,
                        fontSize: '1rem',
                        fontWeight: 600,
                        px: 2
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      fontWeight={600}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}
                    >
                      Priority
                    </Typography>
                    <Chip 
                      label={file.priority} 
                      color={
                        file.priority === 'HIGH' ? 'error' :
                        file.priority === 'MEDIUM' ? 'warning' :
                        'success'
                      }
                      sx={{ 
                        height: 36,
                        fontSize: '1rem',
                        fontWeight: 600,
                        px: 2
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Timestamps Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.secondary.main, 0.08)
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <AccessTimeIcon color="secondary" />
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  color="secondary.main"
                >
                  Timestamps
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="Created At" 
                    value={file.createdAt ? format(new Date(file.createdAt), 'dd MMM yyyy, hh:mm a') : '-'}
                    icon={<AccessTimeIcon sx={{ fontSize: 20, color: 'secondary.main' }} />}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow 
                    label="Last Updated" 
                    value={file.updatedAt ? format(new Date(file.updatedAt), 'dd MMM yyyy, hh:mm a') : '-'}
                    icon={<AccessTimeIcon sx={{ fontSize: 20, color: 'secondary.main' }} />}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Additional Information - Remarks */}
          {file.remarks && (
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.08)
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <InfoIcon color="success" />
                  <Typography 
                    variant="h5" 
                    fontWeight={600}
                    color="success.main"
                  >
                    Remarks
                  </Typography>
                </Stack>

                <Box sx={{ 
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.8,
                      fontSize: '1.1rem'
                    }}
                  >
                    {file.remarks}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Status Workflow */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <FileStatusWorkflow 
                file={file} 
                onStatusUpdate={onStatusChange}
              />
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default MRDFileDetail